import { useState, useEffect, useMemo } from "react";
import { Box, Grid, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors } from "./services.styles";
import ReportLoader from "../../Reports/Loader";
import ServiceMetricsCards from "./ServiceMetricsCards";
import ServiceBookingsTrend from "./ServiceBookingsTrend";
import ServiceDropOffAnalysis from "./ServiceDropOffAnalysis";
import DetailedServicePerformance from "./DetailedServicePerformance";
import { getServicePerformanceApi } from "../../../api/leads.api";
import dayjs from "dayjs";

const Services = ({ dateRange, onDateChange }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Default date range if not provided
  const effectiveDateRange = useMemo(
    () =>
      dateRange || {
        startDate: dayjs().startOf("month").toDate(),
        endDate: dayjs().endOf("month").toDate(),
      },
    [dateRange],
  );

  // Fetch data when dateRange changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const startDate = effectiveDateRange.startDate.toISOString();
        const endDate = effectiveDateRange.endDate.toISOString();

        const response = await getServicePerformanceApi({
          startDate,
          endDate,
        });
        setData(response.data?.data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching service performance:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateRange]);

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Filter performance data based on applied filters
  const filteredPerformanceData = useMemo(() => {
    if (!data?.services) return [];
    if (!filters.categories || filters.categories.length === 0) {
      return data.services;
    }
    return data.services.filter((item) =>
      filters.categories.some((cat) =>
        item.name.toLowerCase().includes(cat.toLowerCase()),
      ),
    );
  }, [data, filters]);

  // Compute metrics dynamically from services data
  const metricsData = useMemo(() => {
    const services = data?.services || [];
    const summary = data?.summary || {};

    if (services.length === 0 && !summary.totalLeads) return null;

    // Highest Revenue — service with max revenue (pick first when all equal/zero)
    const highestRevenueService = services.reduce(
      (max, s) => (max === null || s.revenue > max.revenue ? s : max),
      null,
    );

    // Highest Conversion — service with max conversion rate (pick first when all equal/zero)
    const highestConvService = services.reduce(
      (max, s) => (max === null || s.convRate > max.convRate ? s : max),
      null,
    );

    // Totals from services array
    const totalRevenue = services.reduce(
      (sum, s) => sum + (s.revenue || 0),
      0,
    );
    const totalLeads = services.reduce(
      (sum, s) => sum + (s.totalLeads || 0),
      0,
    );

    // Avg Service Value — total revenue / total leads
    const avgServiceValue =
      totalLeads > 0 ? Math.round(totalRevenue / totalLeads) : 0;

    // Find the service with most leads (useful when revenue is 0)
    const mostLeadsService = services.reduce(
      (max, s) => (max === null || s.totalLeads > max.totalLeads ? s : max),
      null,
    );

    return {
      highestRevenue: {
        name:
          summary.highestRevenue?.name ||
          highestRevenueService?.name ||
          "No data",
        revenue:
          summary.highestRevenue?.revenue ||
          highestRevenueService?.revenue ||
          0,
        totalLeads: highestRevenueService?.totalLeads || 0,
      },
      highestConversion: {
        name:
          summary.highestConversion?.name ||
          highestConvService?.name ||
          "No data",
        rate:
          summary.highestConversion?.rate ||
          highestConvService?.convRate ||
          0,
        totalLeads: highestConvService?.totalLeads || 0,
      },
      avgServiceValue: summary.avgServiceValue || avgServiceValue,
      totalRevenue: summary.totalRevenue || totalRevenue,
      totalLeads: summary.totalLeads || totalLeads,
      totalWon: summary.totalWon || 0,
      totalDropped: summary.totalDropped || 0,
      conversionRate: summary.conversionRate || 0,
      totalServices: services.length,
      mostLeadsService: mostLeadsService?.name || "",
      mostLeadsCount: mostLeadsService?.totalLeads || 0,
    };
  }, [data]);

  // Prepare bookings trend data from API - now fully dynamic
  const bookingsTrendData = data?.timeSeriesData?.data || [];

  // Prepare drop-off data from API - now dynamic based on actual stages
  const dropOffData = data?.dropOffData || [];

  if (loading) {
    return <ReportLoader />;
  }

  // Main services view
  return (
    <Box
      sx={{
        bgcolor: colors.bgPrimary,
      }}
    >
      <Stack>
        {/* Metrics Cards */}
        <ServiceMetricsCards data={metricsData} />

        {/* Charts Row - Bookings Trend and Drop-off Analysis */}
        <Grid container spacing={1.5} sx={{ alignItems: "stretch", mt: 1.5 }}>
          <Grid size={{ xs: 12, lg: 7 }} sx={{ display: "flex" }}>
            <ServiceBookingsTrend data={data?.timeSeriesData} />
          </Grid>
          <Grid size={{ xs: 12, lg: 5 }} sx={{ display: "flex" }}>
            <ServiceDropOffAnalysis data={data?.services || []} />
          </Grid>
        </Grid>

        {/* Detailed Service Performance Table */}
        <Box sx={{ mt: 1.5 }}>
          <DetailedServicePerformance
            data={filteredPerformanceData}
            allServices={data?.allServices || []}
            onFilterChange={handleFilterChange}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default Services;
